import { useState } from 'react';
import type { FC } from 'react';
import type { PricingResult, BoxForm, LayerResult } from '../lib/types';
import { formatINR } from '../lib/constants';
import { s } from '../lib/styles';
import VendorQuoteCard from './VendorQuoteCard';

interface Props {
  result: PricingResult;
  form: BoxForm;
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onApprove: () => void;
  onBack: () => void;
}

interface LayerBreakdownProps {
  layers: LayerResult[];
}

const Page3_Results: FC<Props> = ({
  result,
  form,
  selectedIds,
  onToggle,
  onApprove,
  onBack,
}) => {
  const [showLayers, setShowLayers] = useState(false);

  const {
    box_calculation: calc,
    volume_calculation: vol,
    print_calculation: print,
    vendor_quotes,
    recommended_vendor,
    recommendation_reason,
    cost_saving_tip,
    assumptions,
  } = result;

  // Sort vendors cheapest first
  const sortedQuotes = [...vendor_quotes].sort(
    (a, b) => a.price_per_unit - b.price_per_unit
  );

  return (
    <div style={s.body}>
      {/* Summary bar */}
      <div style={s.summaryBar}>
        {(
          [
            ['Box', `${form.length}×${form.width}×${form.height}cm`],
            ['Qty', Number(form.quantity).toLocaleString()],
            ['To', form.location],
            ['Weight', `${Number(calc.total_box_weight_g).toFixed(1)}g`],
            ['Volume', `${vol?.total_volume_m3?.toFixed(2)} m³`],
            ['Vehicle', vol?.selected_vehicle ?? '—'],
          ] as [string, string][]
        ).map(([label, value], i, arr) => (
          <>
            <div key={label} style={s.summaryItem}>
              <span
                style={{
                  fontSize: 9,
                  color: '#94a3b8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                {label}
              </span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#1e293b' }}>
                {value}
              </span>
            </div>
            {i < arr.length - 1 && (
              <div key={`dot-${i}`} style={s.summaryDot} />
            )}
          </>
        ))}
      </div>

      {/* Volume + print info cards */}
      <div style={s.row2}>
        <div
          style={{
            ...s.card,
            background: '#fffbeb',
            borderColor: '#fde68a',
            padding: 12,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#92400e',
              marginBottom: 5,
            }}
          >
            🚛 Shipping basis
          </div>
          <div style={{ fontSize: 10, color: '#a16207', lineHeight: 1.7 }}>
            {vol?.volume_per_box_m3?.toFixed(4)} m³/box
            <br />~{vol?.estimated_thickness_mm}mm thick (flat-packed)
            <br />
            <span style={{ fontSize: 9, color: '#b45309' }}>
              {vol?.thickness_reasoning}
            </span>
          </div>
        </div>
        {(print?.colour_multiplier ?? 0) > 0 && (
          <div
            style={{
              ...s.card,
              background: '#faf5ff',
              borderColor: '#e9d5ff',
              padding: 12,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: '#6b21a8',
                marginBottom: 5,
              }}
            >
              🖨 Print basis
            </div>
            <div style={{ fontSize: 10, color: '#7e22ce', lineHeight: 1.7 }}>
              Outer liner: {print!.outer_liner_gsm} GSM
              <br />
              Weight: {print!.outer_liner_weight_kg?.toFixed(4)} kg/box
              <br />₹{print!.print_rate_per_kg}/kg × {print!.colour_multiplier}{' '}
              = {formatINR(print!.print_cost_per_unit)}/unit
            </div>
          </div>
        )}
      </div>

      {/* Assumptions */}
      {assumptions.length > 0 && (
        <div style={s.assumptionBox}>
          <div
            style={{
              color: '#c2410c',
              fontWeight: 700,
              fontSize: 11,
              marginBottom: 5,
            }}
          >
            ✦ AI made {assumptions.length} assumption
            {assumptions.length > 1 ? 's' : ''}
          </div>
          {assumptions.map((a, i) => (
            <div
              key={i}
              style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}
            >
              · Layer {a.layer}{' '}
              <strong style={{ color: '#1e293b' }}>{a.field}</strong>
              {' → '}
              <strong style={{ color: '#c2410c' }}>{a.assumed_value}</strong>
              <span style={{ color: '#94a3b8' }}> ({a.reason})</span>
            </div>
          ))}
        </div>
      )}

      {/* Layer breakdown toggle */}
      <button
        onClick={() => setShowLayers((v) => !v)}
        style={s.breakdownToggle}
      >
        {showLayers ? '▲ Hide' : '▼ Show'} layer breakdown
      </button>
      {showLayers && <LayerBreakdown layers={calc.layers} />}

      {/* Selection hint */}
      <div
        style={{
          fontSize: 11,
          color: '#64748b',
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 8,
          padding: '8px 12px',
        }}
      >
        ☑ Click vendor cards to select, then click{' '}
        <strong>Approve Selected</strong> below.
      </div>

      {/* Vendor cards — sorted cheapest first */}
      <div style={{ ...s.sectionLabel, marginBottom: 6 }}>
        🏭 Vendor Quotes{' '}
        <span style={{ color: '#2563eb', fontWeight: 700 }}>
          ({selectedIds.size} selected)
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sortedQuotes.map((quote, i) => (
          <VendorQuoteCard
            key={quote.id}
            quote={quote}
            index={i}
            isRecommended={quote.name === recommended_vendor}
            isSelected={selectedIds.has(quote.id)}
            onToggleSelect={() => onToggle(quote.id)}
            quantity={form.quantity}
            volumeCalc={vol}
            printCalc={print}
          />
        ))}
      </div>

      {/* Recommendation */}
      {recommendation_reason && (
        <div style={s.recommendBox}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#2563eb',
              marginBottom: 4,
            }}
          >
            ✦ Why {recommended_vendor}?
          </div>
          <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.6 }}>
            {recommendation_reason}
          </div>
        </div>
      )}

      {cost_saving_tip && (
        <div style={s.tipBox}>
          <span>💡</span>
          <span>
            <strong>Cost tip:</strong> {cost_saving_tip}
          </span>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: 'flex', gap: 9 }}>
        <button onClick={onBack} style={s.backBtn}>
          ← Edit Config
        </button>
        <button
          onClick={onApprove}
          disabled={selectedIds.size === 0}
          style={{
            ...s.approveBtn,
            flex: 1,
            opacity: selectedIds.size === 0 ? 0.45 : 1,
            cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          {selectedIds.size === 0
            ? 'Select vendors to approve'
            : `✦ Approve ${selectedIds.size} Vendor${selectedIds.size > 1 ? 's' : ''}`}
        </button>
      </div>
    </div>
  );
};

// ─── SUB-COMPONENT ────────────────────────────────────────────────────────────

const LayerBreakdown: FC<LayerBreakdownProps> = ({ layers }) => {
  const COLS = [
    { label: 'Lyr', width: '32px' },
    { label: 'Role', width: '52px' },
    { label: 'Material', width: '1fr' },
    { label: 'GSM', width: '40px' },
    { label: 'Takeup', width: '48px' },
    { label: 'Weight', width: '58px' },
  ];
  const grid = COLS.map((c) => c.width).join(' ');

  return (
    <div style={s.breakdownTable}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: grid,
          gap: 5,
          paddingBottom: 6,
          borderBottom: '1px solid #e2e8f0',
          marginBottom: 4,
        }}
      >
        {COLS.map((c) => (
          <div
            key={c.label}
            style={{
              fontSize: 9,
              fontWeight: 700,
              color: '#94a3b8',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {c.label}
          </div>
        ))}
      </div>
      {layers.map((row, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: grid,
            gap: 5,
            padding: '4px 0',
            borderBottom: '1px solid #f8fafc',
          }}
        >
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{row.layer}</div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: row.role === 'Flute' ? '#d97706' : '#2563eb',
            }}
          >
            {row.role}
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>{row.material}</div>
          <div style={{ fontSize: 10, color: '#475569' }}>{row.gsm}</div>
          <div style={{ fontSize: 10, color: '#475569' }}>×{row.takeup}</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#1e293b' }}>
            {Number(row.weight_g).toFixed(2)}g
          </div>
        </div>
      ))}
    </div>
  );
};

export default Page3_Results;
